import layoutJson from "./r1cadpcf.json";

/**
 * R1CADPCF — reprodução do relatório Jasper do legado (C:\MOVIDO PARA SERVIDOR\FORMSTEEL\JASPER\R1CADPCF.jrxml).
 * O layout (r1cadpcf.json) é EXTRAÍDO do .jrxml: cada caixa, linha, texto, imagem e código de barras na mesma
 * posição/tamanho em pontos. Bandas: cabeçalho do grupo (1ª folha), uma banda de detalhe por etapa e rodapé
 * em toda folha — mesma paginação do Jasper (FOLHA n de N).
 */

type Linha = Record<string, unknown>;
interface El {
  t: "txt" | "fld" | "line" | "rect" | "img" | "barcode";
  x: number; y: number; w: number; h: number;
  bg?: string; fg?: string; when?: string;
  ha?: string; va?: string; fs?: number; b?: number; i?: number; rot?: string;
  bd?: [number, number, number, number];
  leftPadding?: number; rightPadding?: number; topPadding?: number; bottomPadding?: number; padding?: number;
  text?: string; expr?: string; pattern?: string; blank?: number; evalTime?: string;
  lw?: number; ls?: string; dir?: string; scale?: string;
}
interface Banda { h: number; els: El[] }
interface Layout {
  page: { pageWidth: number; pageHeight: number; leftMargin: number; rightMargin: number; topMargin: number; bottomMargin: number };
  grupo: Banda; detalhe: Banda; rodape: Banda;
  campos: Record<string, string>; // classe Java de cada campo do .jrxml (BigDecimal, String…)
}
const L = layoutJson as unknown as Layout;

// ---------------------------------------------------------------- Code 39 (barbecue, sem texto)
const C39: Record<string, string> = {
  "0": "nnnwwnwnn", "1": "wnnwnnnnw", "2": "nnwwnnnnw", "3": "wnwwnnnnn", "4": "nnnwwnnnw",
  "5": "wnnwwnnnn", "6": "nnwwwnnnn", "7": "nnnwnnwnw", "8": "wnnwnnwnn", "9": "nnwwnnwnn",
  "*": "nwnnwnwnn",
};
function Code39({ valor, w, h }: { valor: string; w: number; h: number }) {
  const barras: { x: number; w: number }[] = [];
  let x = 0;
  for (const ch of ("*" + valor + "*").split("")) {
    const p = C39[ch];
    if (!p) continue;
    for (let k = 0; k < 9; k++) {
      const largura = p[k] === "w" ? 3 : 1;
      if (k % 2 === 0) barras.push({ x, w: largura });
      x += largura;
    }
    x += 1; // espaço entre caracteres
  }
  return (
    <svg width={`${w}pt`} height={`${h}pt`} viewBox={`0 0 ${x} 10`} preserveAspectRatio="none" style={{ display: "block" }}>
      {barras.map((b, k) => (
        <rect key={k} x={b.x} y={0} width={b.w} height={10} fill="#000" />
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------- expressões e máscaras (Java/Jasper)
const fmt = (n: number, casas: number, agrupar = true) =>
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas, useGrouping: agrupar }).format(n);
// Jasper só aplica a máscara (pattern) em campo numérico; em campo String ela é ignorada.
function aplicarPadrao(v: unknown, pattern: string | undefined, classe: string | undefined): string {
  if (v === null || v === undefined) return "";
  const n = Number(v);
  if (!pattern || classe === "String" || String(v).trim() === "" || Number.isNaN(n)) return String(v);
  const casas = (pattern.split(".")[1] || "").length;
  // Padrão BR: máscara sem decimais ("#,###"/"#,##0") aqui só cai em identificador (numpcf, numped,
  // numeop) — identificador não leva separador de milhar.
  return fmt(n, casas, casas > 0);
}
function campo(row: Linha, nome: string): unknown {
  return row[nome];
}
/** Avalia as poucas formas de expressão usadas no R1CADPCF. */
function avaliar(expr: string, row: Linha, pagina: number, total: number): { valor: unknown; texto: boolean } {
  const e = expr.trim();
  const m = e.match(/^\$F\{(\w+)\}$/);
  if (m) return { valor: campo(row, m[1]!), texto: false };
  if (e === "$V{PAGE_NUMBER}") return { valor: String(total), texto: true };
  // concatenação de strings/campos/variáveis (Groovy): 'a' + $F{x} + ...
  const partes = e.match(/'[^']*'|\$F\{\w+\}|\$V\{\w+\}/g) || [];
  const out = partes.map((p) => {
    const s = p.match(/^'(.*)'$/);
    if (s) return s[1]!;
    const f = p.match(/^\$F\{(\w+)\}$/);
    if (f) {
      const v = campo(row, f[1]!);
      if (v === null || v === undefined) return "null";
      // BigDecimal concatenado vem do servidor como texto com ponto ("0.30") — exibe com vírgula (pt-BR)
      return L.campos[f[1]!] !== "String" && /^-?\d+\.\d+$/.test(String(v)) ? String(v).replace(".", ",") : String(v);
    }
    if (p === "$V{PAGE_NUMBER}") return String(pagina);
    return "";
  });
  return { valor: out.join(""), texto: true };
}
function quando(cond: string | undefined, row: Linha): boolean {
  if (!cond) return true;
  const m = cond.match(/\$F\{(\w+)\}\s*!=\s*null/);
  if (m) return row[m[1]!] !== null && row[m[1]!] !== undefined;
  return true;
}

// ---------------------------------------------------------------- elementos
function Elemento({ el, row, pagina, total, dy }: { el: El; row: Linha; pagina: number; total: number; dy: number }) {
  if (!quando(el.when, row)) return null;
  const base: React.CSSProperties = {
    position: "absolute", left: `${el.x}pt`, top: `${el.y + dy}pt`, width: `${el.w}pt`, height: `${el.h}pt`,
    boxSizing: "border-box",
  };
  if (el.t === "line") {
    const lw = el.lw ?? 1;
    const vertical = el.h > el.w;
    return (
      <div
        style={{
          ...base,
          ...(vertical ? { width: 0, borderLeft: `${lw}pt ${el.ls === "Dashed" ? "dashed" : "solid"} #000` }
            : { height: 0, borderTop: `${lw}pt ${el.ls === "Dashed" ? "dashed" : "solid"} #000` }),
        }}
      />
    );
  }
  if (el.t === "rect") return <div style={{ ...base, border: "1pt solid #000", background: el.bg }} />;
  if (el.t === "img")
    return <img src="/r1cadpcf-logo.jpg" alt="" style={{ ...base, objectFit: el.scale === "FillFrame" ? "fill" : "contain", objectPosition: "left top" }} />;
  if (el.t === "barcode") {
    const cod = String(row.codbar ?? "");
    return <div style={base}>{cod && <Code39 valor={cod} w={el.w} h={el.h} />}</div>;
  }
  let texto = el.text ?? "";
  if (el.t === "fld") {
    const r = avaliar(el.expr ?? "", row, pagina, total);
    if (r.valor === null || r.valor === undefined) texto = el.blank ? "" : "null";
    else texto = r.texto ? String(r.valor) : aplicarPadrao(r.valor, el.pattern, L.campos[(el.expr ?? "").replace(/^\$F\{(\w+)\}$/, "$1")]);
  }
  const [bt, br, bb, bl] = el.bd ?? [0, 0, 0, 0];
  const pad = el.padding ?? 0;
  return (
    <div
      style={{
        ...base,
        background: el.bg,
        color: el.fg ?? "#000",
        borderStyle: "solid",
        borderColor: "#000",
        borderWidth: `${bt}pt ${br}pt ${bb}pt ${bl}pt`,
        paddingLeft: `${el.leftPadding ?? pad}pt`,
        paddingRight: `${el.rightPadding ?? pad}pt`,
        paddingTop: `${el.topPadding ?? pad}pt`,
        paddingBottom: `${el.bottomPadding ?? pad}pt`,
        fontFamily: "Arial, Helvetica, sans-serif",
        fontSize: `${el.fs ?? 10}pt`,
        fontWeight: el.b ? 700 : 400,
        fontStyle: el.i ? "italic" : "normal",
        lineHeight: 1.15,
        display: "flex",
        flexDirection: "column",
        // texto que não cabe: o Jasper mostra as primeiras linhas e corta o resto ("safe")
        justifyContent: el.va === "Middle" ? "safe center" : el.va === "Bottom" ? "safe flex-end" : "flex-start",
        textAlign: (el.ha === "Center" ? "center" : el.ha === "Right" ? "right" : el.ha === "Justified" ? "justify" : "left") as React.CSSProperties["textAlign"],
        overflow: "hidden",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      <span>{texto}</span>
    </div>
  );
}

function Banda({ banda, row, pagina, total, dy }: { banda: Banda; row: Linha; pagina: number; total: number; dy: number }) {
  return (
    <>
      {banda.els.map((el, k) => (
        <Elemento key={k} el={el} row={row} pagina={pagina} total={total} dy={dy} />
      ))}
    </>
  );
}

/** Distribui as bandas nas folhas como o Jasper: cabeçalho do grupo só na 1ª folha, rodapé em todas. */
function paginar(linhas: Linha[]): { cabecalho: boolean; detalhes: Linha[] }[] {
  const util = L.page.pageHeight - L.page.topMargin - L.page.bottomMargin;
  const limite = util - L.rodape.h;
  const folhas: { cabecalho: boolean; detalhes: Linha[] }[] = [];
  let atual = { cabecalho: true, detalhes: [] as Linha[] };
  let y = L.grupo.h;
  for (const l of linhas) {
    if (y + L.detalhe.h > limite) {
      folhas.push(atual);
      atual = { cabecalho: false, detalhes: [] };
      y = 0;
    }
    atual.detalhes.push(l);
    y += L.detalhe.h;
  }
  folhas.push(atual);
  return folhas;
}

export function R1CadPcf({ linhas }: { linhas: Linha[] }) {
  if (!linhas.length) return null;
  const folhas = paginar(linhas);
  const total = folhas.length;
  const util = L.page.pageHeight - L.page.topMargin - L.page.bottomMargin;
  return (
    <div className="r1-folhas">
      {folhas.map((f, idx) => {
        const pagina = idx + 1;
        let y = f.cabecalho ? L.grupo.h : 0;
        return (
          <div key={idx} className="r1-folha" style={{ width: `${L.page.pageWidth}pt`, height: `${L.page.pageHeight}pt` }}>
            <div style={{ position: "absolute", left: `${L.page.leftMargin}pt`, top: `${L.page.topMargin}pt`,
              width: `${L.page.pageWidth - L.page.leftMargin - L.page.rightMargin}pt`, height: `${util}pt` }}>
              {f.cabecalho && <Banda banda={L.grupo} row={linhas[0]!} pagina={pagina} total={total} dy={0} />}
              {f.detalhes.map((l, k) => {
                const dy = y;
                y += L.detalhe.h;
                return <Banda key={k} banda={L.detalhe} row={l} pagina={pagina} total={total} dy={dy} />;
              })}
              <Banda banda={L.rodape} row={linhas[0]!} pagina={pagina} total={total} dy={util - L.rodape.h} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
